import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { oauthAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";

const credential = productVariant({
  authLabel: "Server-to-server OAuth",
  identifier: "zoom",
  keywords: "meetings webinars conferencing calls recordings participants",
  protocol: "http",
  defaultUrlPattern: () => "https://api\\.zoom\\.us/.*",
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: "https://zoom.us/oauth/token",
        method: "POST",
        formBody: {
          grant_type: "account_credentials",
          account_id: app.config.accountId,
        },
        basicAuth: {
          username: app.secret.clientId,
          password: app.secret.clientSecret,
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: { kind: "header", scheme: "Bearer" },
    }),
  steps: [
    {
      title: "Create a Server-to-Server OAuth app",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Zoom App Marketplace](https://marketplace.zoom.us/develop/create) and create a new **Server-to-Server OAuth** app.",
        },
      ],
    },
    {
      title: "Copy your credentials",
      items: [
        {
          kind: "markdown",
          content:
            "From your app’s credentials page, copy the account ID, client ID, and client secret.",
        },
        {
          kind: "input",
          label: "Account ID",
          required: true,
          target: { kind: "configuration", field: "accountId" },
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          target: { kind: "secrets", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "secrets", field: "clientSecret" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: () => "https://api.zoom.us/v2/users/me",
  },
});

export const zoom = defineProduct({
  name: "Zoom",
  tagLine: "Video communications platform",
  logo,
  tags: ["communication"],
  variants: [
    credential,
    productVariant({
      identifier: "zoomOAuth",
      authLabel: "OAuth",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.zoom\\.us/v2/.*",
      auth: oauthAuth,
      steps: oauthSetupSteps({
        identifier: "zoomOAuth",
        registrationUrl: "https://marketplace.zoom.us/develop/create",
        scopes: ["user:read:user"],
        instructions:
          "Create a General App with user authorization and enable the user:read:user scope. Add any granular scopes needed by your workflow.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://zoom.us/oauth/authorize",
        tokenEndpoint: () => "https://zoom.us/oauth/token",
        tokenEndpointAuthMethod: () => "client_secret_basic",
        clientCredentialsFrom: "db",
        scopes: () => ["user:read:user"],
        pkce: () => false,
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.zoom.us/v2/users/me",
      },
    }),
  ],
});
